import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { FormHeader } from "../FormHeader";
import { FormFooter } from "../FormFooter";

const textFieldVariants = cva(
  "relative flex items-center gap-2 border rounded-large transition-all font-display font-size-100 leading-100",
  {
    variants: {
      size: {
        small: "h-[28px] px-3 text-xs gap-2",
        medium: "h-[36px] px-4 text-sm gap-2",
        large: "h-[44px] px-5 text-base gap-3",
      },
      validationState: {
        none: `
        border-action-outline-neutral-faded 
        hover:border-action-outline-primary-hover 
        focus-within:border-action-outline-primary-hover 
        focus-within:ring-2 
        ring-action-outline-primary-faded-hover`,
        positive: `
          border-action-outline-positive-default 
          focus-within:border-action-outline-positive-hover
          focus-within:ring-2
          ring-action-outline-positive-faded-hover`,
        negative: `border-action-outline-negative-default 
          focus-within:border-action-outline-negative-hover
          focus-within:ring-2 
          ring-action-outline-negative-faded-hover`,
      },
      isDisabled: {
        true: `
        border-[var(--border-width-thinner)]
        hover:border-action-outline-neutral-disabled
        border-action-outline-neutral-disabled
        bg-surface-fill-neutral-intense cursor-not-allowed opacity-60`,
        false: "bg-surface-fill-neutral-intense",
      },
    },
    defaultVariants: {
      size: "medium",
      validationState: "none",
      isDisabled: false,
    },
  }
);

export interface TextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "prefix"> {
  label?: string;
  helperText?: string;
  errorText?: string;
  successText?: string;
  size?: "small" | "medium" | "large";
  validationState?: "none" | "positive" | "negative";
  isDisabled?: boolean;
  isRequired?: boolean;
  isOptional?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  showClearButton?: boolean;
  onClear?: () => void;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  infoHeading?: string;
  infoDescription?: string;
  linkText?: string;
  linkHref?: string;
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      helperText,
      errorText,
      successText,
      size = "medium",
      validationState = "none",
      isDisabled = false,
      isRequired = false,
      isOptional = false,
      prefix,
      suffix,
      showClearButton = false,
      infoDescription,
      infoHeading,
      linkText,
      linkHref,
      onLinkClick,
      onClear,
      containerClassName,
      labelClassName,
      inputClassName,
      className,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState("");
    const inputValue = value !== undefined ? value : internalValue;
    const hasValue = inputValue && String(inputValue).length > 0;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      } else {
        setInternalValue(e.target.value);
      }
    };

    const handleClear = () => {
      if (onClear) {
        onClear();
      } else {
        setInternalValue("");
      }
      // Focus the input after clearing
      const input = document.getElementById(props.id || "");
      if (input) {
        input.focus();
      }
    };

    // Determine which helper text to show
    const displayHelperText = errorText || successText || helperText;
    const currentValidationState = errorText
      ? "negative"
      : successText
      ? "positive"
      : validationState;

    const sizeConfig = {
      small: {
        gap: "gap-2",
      },
      medium: {
        gap: "gap-2",
      },
      large: {
        gap: "gap-3",
      },
    };

    return (
      <div
        className={cn(
          "w-full flex flex-col",
          sizeConfig[size].gap,
          containerClassName
        )}
      >
        {label && (
          <FormHeader
            label={label}
            size={size}
            isRequired={isRequired}
            isOptional={isOptional}
            infoHeading={infoHeading}
            infoDescription={infoDescription}
            linkText={linkText}
            linkHref={linkHref}
            onLinkClick={onLinkClick}
            htmlFor={props.id}
            className="mb-2"
            labelClassName={labelClassName}
          />
        )}

        <div
          className={cn(
            textFieldVariants({
              size,
              validationState: currentValidationState,
              isDisabled,
            }),
            className
          )}
        >
          {prefix && (
            <span
              className={cn(
                "shrink-0 flex items-center",
                isDisabled
                  ? "text-surface-ink-neutral-disabled"
                  : currentValidationState === "positive"
                  ? "text-feedback-ink-positive-intense"
                  : currentValidationState === "negative"
                  ? "text-feedback-ink-negative-subtle"
                  : "text-surface-ink-neutral-muted"
              )}
            >
              {prefix}
            </span>
          )}

          <input
            ref={ref}
            value={inputValue}
            onChange={handleChange}
            disabled={isDisabled}
            required={isRequired}
            className={cn(
              "flex-1 bg-transparent border-none outline-none text-surface-ink-neutral-normal placeholder:text-surface-ink-neutral-muted disabled:cursor-not-allowed disabled:text-surface-ink-neutral-disabled font-display",
              inputClassName
            )}
            {...props}
          />

          {showClearButton && hasValue && !isDisabled && (
            <button
              type="button"
              onClick={handleClear}
              className="shrink-0 flex items-center justify-center text-surface-ink-neutral-muted hover:text-surface-ink-neutral-normal transition-colors"
              tabIndex={-1}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 4L4 12M4 4L12 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}

          {suffix && (
            <span
              className={cn(
                "shrink-0 flex items-center",
                isDisabled
                  ? "text-surface-ink-neutral-disabled"
                  : currentValidationState === "positive"
                  ? "text-feedback-ink-positive-intense"
                  : currentValidationState === "negative"
                  ? "text-feedback-ink-negative-subtle"
                  : "text-surface-ink-neutral-muted"
              )}
            >
              {suffix}
            </span>
          )}
        </div>

        <FormFooter
          helperText={displayHelperText}
          validationState={
            currentValidationState === "none"
              ? "default"
              : currentValidationState
          }
          size={size}
          isDisabled={isDisabled}
          className="mt-1"
        />
      </div>
    );
  }
);

TextField.displayName = "TextField";

export { TextField, textFieldVariants };
